import { useEffect } from 'react';

import { getUserData, ISADMINLOGGED, onLogout } from '../constants/global';
import { sendLogoutBeacon } from '../lib/logout-beacon';

/**
 * Logs the agent out when the browser/tab is closed, while keeping them signed
 * in across a page refresh.
 *
 * The browser fires the *same* `pagehide`/`beforeunload` event for a close and
 * a refresh, so they can't be told apart at unload time. We therefore use a
 * deferred, reopen-flush model: on unload we only stash a marker (current token
 * + timestamp) in localStorage. On the next load we decide what happened:
 *
 *  - reloaded quickly (within GRACE_MS) or nav-type is `reload` → it was a
 *    refresh (or a fast in-app `window.open(_self)`) → discard the marker.
 *  - the tab was gone longer → a real close that the user later reopened →
 *    fire a best-effort backend logout for the stashed token + clear client
 *    state, forcing a fresh login.
 *
 * Known limitations (acceptable for v1):
 *  - Close-without-reopen: no JS runs after a true close, so the backend can't
 *    be invalidated then. The cookie is a session cookie (already gone on the
 *    client) and the backend session expires on its own TTL.
 *  - Multi-tab: closing one of several tabs writes the marker; a later load in
 *    another tab after the grace window could flush it. A BroadcastChannel
 *    tab-counter could refine this later.
 */

const PENDING_LOGOUT_KEY = 'ielect_pending_logout';
const GRACE_MS = 5000;

interface PendingLogout {
	ts: number;
	token?: string;
	partyId?: string;
}

const wasReload = () => {
	try {
		const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
		return nav?.type === 'reload' || nav?.type === 'back_forward';
	} catch {
		return false;
	}
};

const flushPendingLogout = () => {
	const raw = localStorage.getItem(PENDING_LOGOUT_KEY);
	if (!raw) return;

	let pending: PendingLogout | null = null;
	try {
		pending = JSON.parse(raw) as PendingLogout;
	} catch {
		pending = null;
	}

	// A refresh (or fast in-app navigation) reloads within the grace window.
	const isRefresh = !pending || wasReload() || Date.now() - pending.ts <= GRACE_MS;

	if (isRefresh) {
		localStorage.removeItem(PENDING_LOGOUT_KEY);
		return;
	}

	// Real close + reopen: read the stashed token before onLogout() wipes storage.
	sendLogoutBeacon(pending?.token, pending?.partyId);
	onLogout(); // clears the cookie + localStorage (incl. the pending key)
	localStorage.removeItem(PENDING_LOGOUT_KEY);
};

export const useBrowserCloseLogout = () => {
	useEffect(() => {
		// Runs on every load (any route, incl. login): decide refresh vs. close.
		flushPendingLogout();

		const stashPendingLogout = () => {
			if (!ISADMINLOGGED()) return;
			const data: PendingLogout = {
				ts: Date.now(),
				token: getUserData().token,
				partyId: getUserData().user?.PartyId,
			};
			// Don't touch the cookie — let the session cookie die on a real close,
			// and survive refresh untouched.
			localStorage.setItem(PENDING_LOGOUT_KEY, JSON.stringify(data));
		};

		window.addEventListener('pagehide', stashPendingLogout);
		window.addEventListener('beforeunload', stashPendingLogout);

		return () => {
			window.removeEventListener('pagehide', stashPendingLogout);
			window.removeEventListener('beforeunload', stashPendingLogout);
		};
	}, []);
};
